import ClickAwayListener from '@material-ui/core/ClickAwayListener'
import { isSameDay } from 'date-fns'
import React, { useCallback, useEffect, useLayoutEffect } from 'react'
import { FiCheck, FiMaximize2, FiMinimize2 } from 'react-icons/fi'
import Flex from 'typescript-styled-flex'
import {
  DayPicker,
  MonthPicker,
  Triangle,
  YearPicker,
} from '../../../../components/Calendar/DatePicker'
import DayPickerSized from '../../../../components/Calendar/DatePicker/DayPickerSized'
import { SlideWrapper } from '../../../../components/Calendar/DatePicker/SlideWrapper'
import {
  SizeButton,
  SubmitButton,
} from '../../../../components/Calendar/DatePicker/styled'
import { isSafari } from '../../../../components/DetectBrowser/DetectBrowser'
import { is } from '../../../../components/MediaQuery'
import ClickAwayBackground from '../../FilterDrawer/Drawer/components/ClickAwayBackground'
import DatePickerContainer from './DatePickerContainer'
import DatePickerContent from './DatePickerContent'
import DatePickerHeader from './DatePickerHeader'

function DatePicker({
  onSubmit,
  close,
  startDate,
  show,
  anchorEl,
  onChange,
  startSized,
  offsetTop = 0,
  offsetLeft = 0,
  sizedOnMobile = false,
  disableMobileResizing = false,
  isCenterOnMobile,
  onBlur,
  selectedDay,
  transcendens = false,
  monthOnly = false,
}: IDatePickerProps & IDatePickerDispatch) {
  const isMobile = is.mobile()
  const isHorizontal = is.horizontal()
  const [selectedDate, setSelected] = React.useState(startDate)
  const [sized, setSized] = React.useState(
    isCenterOnMobile ? isCenterOnMobile : startSized,
  )
  const [x, setX] = React.useState(0)
  const [y, setY] = React.useState(0)
  const safari = isSafari()

  useEffect(() => {
    if (isHorizontal && isMobile && transcendens) {
      setSized(true)
    } else if (transcendens) {
      setSized(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHorizontal])

  const calcPosition = useCallback(() => {
    if (null === anchorEl || null === anchorEl.current) {
      return { x: 0, y: 0 }
    }

    const left = anchorEl.current.offsetLeft
    const top = anchorEl.current.offsetTop
    const width = anchorEl.current.offsetWidth
    const height = anchorEl.current.offsetHeight

    setX((Math.round(left + offsetLeft)
      - 150
      + width / 2
      + (sized || (isMobile && sizedOnMobile) ? 60 : 0)))
    setY((Math.round(top + offsetTop) + height))
  }, [anchorEl, isMobile, offsetLeft, offsetTop, sized, sizedOnMobile])

  useEffect(() => {
    !isSameDay(startDate, selectedDate) && setSelected(startDate)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, show])

  useLayoutEffect(() => {
    calcPosition()
    window.addEventListener('resize', calcPosition)

    return () => {
      window.removeEventListener('resize', calcPosition)
    }
  }, [calcPosition])

  useLayoutEffect(() => {
    calcPosition()
  }, [calcPosition, show])

  function setSelectedDate(date) {
    setTimeout(() => {
      calcPosition()
    }, 100)
    setSelected(date)
    if (onChange) {
      onChange(date)
    }
  }

  function handleDayChange() {
    onSubmit(selectedDate)
    close()
  }

  function size() {
    setSized(!sized)
  }

  if (anchorEl.current === undefined) {
    return <div />
  }

  return (
    <React.Fragment>
      <ClickAwayListener
        onClickAway={close}
        touchEvent={transcendens ? false : 'onTouchEnd'}
        mouseEvent={transcendens ? false : 'onMouseUp'}
      >
        <SlideWrapper
          sized={sized || (isMobile && sizedOnMobile)}
          isCenterOnMobile={isCenterOnMobile}
          // transcendens={transcendens}
          x={x}
          y={y}
          onBlur={onBlur}
          show={show}
        >
          <Flex column={true} alignCenter={true}>
            {isCenterOnMobile ? null : <Triangle />}
            <DatePickerContainer sized={sized || (isMobile && sizedOnMobile)}>
              <DatePickerHeader
                isCenterOnMobile={isCenterOnMobile}
                sized={sized || (isMobile && sizedOnMobile) || monthOnly}
              >
                <MonthPicker
                  sized={sized || (isMobile && sizedOnMobile) || monthOnly}
                  date={selectedDate}
                  change={setSelectedDate}
                />
              </DatePickerHeader>
              <DatePickerContent
                monthOnly={monthOnly}
                sized={sized || (isMobile && sizedOnMobile)}
              >
                {!monthOnly &&
                  (!(sized || (isMobile && sizedOnMobile)) ? (
                    <DayPicker
                      date={selectedDate}
                      change={setSelectedDate}
                      selectedDay={selectedDay}
                    />
                  ) : (
                    <DayPickerSized
                      date={selectedDate}
                      change={setSelectedDate}
                    />
                  ))}
                <YearPicker date={selectedDate} change={setSelectedDate} />
                <SubmitButton onClick={handleDayChange}>
                  <FiCheck />
                </SubmitButton>
                {!((disableMobileResizing || isHorizontal) && isMobile) &&
                  !monthOnly && (
                    <SizeButton onClick={size}>
                      {sized || (isMobile && sizedOnMobile) ? (
                        <FiMinimize2 />
                      ) : (
                        <FiMaximize2 />
                      )}
                    </SizeButton>
                  )}
              </DatePickerContent>
            </DatePickerContainer>
          </Flex>
        </SlideWrapper>
      </ClickAwayListener>

      {/*Safari has really bad rendering*/}
      {show && safari && (
        <ClickAwayBackground
          style={{
            backgroundColor: 'rgba(0,0,0,0.01)',
            position: 'fixed',
            zIndex: 149,
          }}
          onClick={close}
        />
      )}
    </React.Fragment>
  )
}

export default DatePicker

export interface IDatePickerProps {
  startDate: Date
  show: boolean
  anchorEl: React.RefObject<HTMLAnchorElement | HTMLDivElement> | null
  startSized?: boolean
  offsetTop?: number
  disableMobileResizing?: boolean
  offsetLeft?: number
  sizedOnMobile?: boolean
  isCenterOnMobile?: boolean
  selectedDay?: Date
  transcendens?: boolean
  monthOnly?: boolean
}

export interface IDatePickerDispatch {
  onSubmit?: (date: Date) => void
  onChange?: (date: Date) => void
  close?: (e?: any) => void
  onBlur?: (e?: any) => void
}
